import { useState, useEffect } from 'react';
import useConfiguracion from '../../hooks/useConfiguracion';
import { textoPaso } from '../../utils/textosReporte';

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const DIAS_SEMANA = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

const hoySinHora = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Snapshot del momento actual en la forma que usa `ocurridoEn`. Se usa tanto
// al elegir "Ahora mismo" como al montar el paso (que arranca en modo
// "ahora" pero sin fecha resuelta todavía — sin esto, el botón "Continuar"
// queda deshabilitado aunque la tarjeta se vea seleccionada).
export const momentoAhora = () => {
  const ahora = new Date();
  return {
    modo: 'ahora',
    fecha: ahora.toISOString().slice(0, 10),
    hora: String(((ahora.getHours() + 11) % 12) + 1).padStart(2, '0'),
    minuto: String(ahora.getMinutes()).padStart(2, '0'),
    periodo: ahora.getHours() >= 12 ? 'PM' : 'AM',
  };
};

/**
 * Paso 3/5 del reporte: fecha y hora en que ocurrió la incidencia (README
 * diseño, sección 5.4). Este dato es `ocurridoEn` — se envía como
 * `inc_dtFechaIncidente`/`inc_tmHoraIncidente` al backend (Fase 5), nunca se
 * confunde con `inc_dtFechaCreated` (la pone el servidor al guardar).
 * Calendario propio en JS plano — no amerita traer una librería de fechas
 * para una grilla de un mes con "fecha futura deshabilitada".
 */
const PasoFechaHora = ({ ocurridoEn, onCambiar, onContinuar, onVolver }) => {
  const [mesVisible, setMesVisible] = useState(() => {
    const base = ocurridoEn.modo === 'otro' && ocurridoEn.fecha ? new Date(ocurridoEn.fecha) : new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const { configuracion } = useConfiguracion();
  const texto = textoPaso(configuracion, 'fecha', {
    titulo: '¿Cuándo ocurrió?',
    subtitulo: '',
  });

  // El paso arranca en modo "ahora" por defecto pero sin fecha resuelta —
  // sin esto la tarjeta se ve seleccionada y "Continuar" queda deshabilitado.
  useEffect(() => {
    if (ocurridoEn.modo === 'ahora' && !ocurridoEn.fecha) {
      onCambiar(momentoAhora());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const elegirModo = (modo) => {
    if (modo === 'ahora') {
      onCambiar(momentoAhora());
    } else {
      onCambiar({ ...ocurridoEn, modo });
    }
  };

  const diasDelMes = () => {
    const anio = mesVisible.getFullYear();
    const mes = mesVisible.getMonth();
    const primerDiaSemana = new Date(anio, mes, 1).getDay();
    const totalDias = new Date(anio, mes + 1, 0).getDate();
    const celdas = [];
    for (let i = 0; i < primerDiaSemana; i++) celdas.push(null);
    for (let d = 1; d <= totalDias; d++) celdas.push(new Date(anio, mes, d));
    return celdas;
  };

  const esFutura = (fecha) => fecha > hoySinHora();
  const fechaSeleccionada = ocurridoEn.fecha ? new Date(`${ocurridoEn.fecha}T00:00:00`) : null;

  const cambiarMes = (delta) => setMesVisible((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));

  // Swipe horizontal para cambiar de mes — mismo umbral (~40px) que ya usa
  // el carrusel de evidencia del seguimiento, sin librería nueva.
  let touchStartX = null;
  const onTouchStart = (e) => { touchStartX = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX;
    if (delta > 40) cambiarMes(-1);
    else if (delta < -40) cambiarMes(1);
    touchStartX = null;
  };

  return (
    <div className="rw-paso">
      <div className="rw-titulo-fila">
        <h2 className="rw-titulo" style={texto.estilo}>{texto.titulo}</h2>
        <button type="button" className="rw-omitir" onClick={() => { onCambiar(momentoAhora()); onContinuar(); }}>
          Omitir
        </button>
      </div>

      <div className="rw-fila rw-tarjetas-cuando">
        <button
          type="button"
          className={`rw-tarjeta-eleccion ${ocurridoEn.modo === 'ahora' ? 'rw-tarjeta-activa' : ''}`}
          onClick={() => elegirModo('ahora')}
        >
          <strong>Ahora mismo</strong>
          <span>{new Date().toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })}</span>
        </button>
        <button
          type="button"
          className={`rw-tarjeta-eleccion ${ocurridoEn.modo === 'otro' ? 'rw-tarjeta-activa' : ''}`}
          onClick={() => elegirModo('otro')}
        >
          <strong>Otro momento</strong>
          <span>Elegir fecha y hora</span>
        </button>
      </div>

      {ocurridoEn.modo === 'otro' && (
        <>
          <div className="rw-calendario-cabecera">
            <button type="button" onClick={() => cambiarMes(-1)}>‹</button>
            <span>{MESES[mesVisible.getMonth()]} {mesVisible.getFullYear()}</span>
            <button type="button" onClick={() => cambiarMes(1)}>›</button>
          </div>
          <div className="rw-calendario-grid" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
            {DIAS_SEMANA.map((d, i) => <span key={`h-${i}`} className="rw-calendario-dia-header">{d}</span>)}
            {diasDelMes().map((fecha, i) => {
              if (!fecha) return <span key={`vacio-${i}`} />;
              const futura = esFutura(fecha);
              const seleccionado = fechaSeleccionada && fecha.getTime() === fechaSeleccionada.getTime();
              return (
                <button
                  key={fecha.toISOString()}
                  type="button"
                  disabled={futura}
                  className={`rw-calendario-dia ${seleccionado ? 'rw-calendario-dia-activo' : ''}`}
                  onClick={() => onCambiar({ ...ocurridoEn, fecha: fecha.toISOString().slice(0, 10) })}
                >
                  {fecha.getDate()}
                </button>
              );
            })}
          </div>
          <p className="rw-texto-ayuda">No se aceptan fechas futuras.</p>

          <div className="rw-hora-selector">
            <input
              type="text"
              inputMode="numeric"
              maxLength={2}
              className="rw-hora-caja"
              value={ocurridoEn.hora}
              onChange={(e) => onCambiar({ ...ocurridoEn, hora: e.target.value.replace(/\D/g, '').slice(0, 2) })}
            />
            <span>:</span>
            <input
              type="text"
              inputMode="numeric"
              maxLength={2}
              className="rw-hora-caja"
              value={ocurridoEn.minuto}
              onChange={(e) => onCambiar({ ...ocurridoEn, minuto: e.target.value.replace(/\D/g, '').slice(0, 2) })}
            />
            <div className="rw-periodo-toggle">
              <button
                type="button"
                className={ocurridoEn.periodo === 'AM' ? 'rw-periodo-activo' : ''}
                onClick={() => onCambiar({ ...ocurridoEn, periodo: 'AM' })}
              >AM</button>
              <button
                type="button"
                className={ocurridoEn.periodo === 'PM' ? 'rw-periodo-activo' : ''}
                onClick={() => onCambiar({ ...ocurridoEn, periodo: 'PM' })}
              >PM</button>
            </div>
          </div>
        </>
      )}

      <div className="rw-pie-fijo rw-pie-con-volver">
        <button type="button" className="rw-boton-secundario" onClick={onVolver}>Atrás</button>
        <button
          type="button"
          className="rw-boton-primario"
          disabled={!ocurridoEn.fecha}
          onClick={onContinuar}
        >
          Continuar
        </button>
      </div>
    </div>
  );
};

export default PasoFechaHora;
